import React, { useState, useEffect } from "react";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/core/styles";
import { formatPrice, isEmpty, roundUp } from "../../utils/FrontEndHelpers";
import styled from "styled-components";
import VehicleService from "../../services/VehicleService";
import { CircularProgress } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: "0 10px",
  },
  addressDiv: {
    marginRight: theme.spacing(1),
  },
  lotDiv: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  plateNumberInput: {
    width: "160px",
  },
  plateStateInput: {
    width: "120px",
  },
  hoursInput: {
    width: "90px",
    height: "51px",
    marginLeft: "6px !important",
  },
  licenseInputDiv: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: "20px",
  },
}));

const SessionInfoPage = ({ zoneObj, handleNext, setSession, sessionData }) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [feeState, setFeeState] = useState("");
  const [estimatedPrice, setEstimatedPrice] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  // const [colors, setColors] = useState({
  //   1: true,
  //   2: false,
  //   3: false,
  //   4: false,
  //   5: false,
  // });

  const feeAmount =
    zoneObj.zone.price < 10 ? 0.35 : zoneObj.zone.price > 100 ? 0.07 : 0.1; // this is a percentage either 10% or 35% of the rate // changing to 7% for all zones
  const { hours, isHourly, vehiclePlate, vehicleState, rate } = sessionData;

  const setUpCosts = (givenHour) => {
    const f = roundUp(
      zoneObj.zone.isHourly
        ? zoneObj.zone.price * givenHour * feeAmount
        : zoneObj.zone.price * feeAmount
    ).toFixed(2);

    // exception added to make all zones that are 5 or less to have a fee of 1.75
    // const fee = zoneObj.zone.price < 5 ? 1.75 : f;

    setFeeState(f);

    if (zoneObj.zone.isHourly) {
      const calcRate = zoneObj.zone.isHourly
        ? zoneObj.zone.price * givenHour
        : zoneObj.zone.price;
      const calcFee = zoneObj.zone.isHourly
        ? zoneObj.zone.price * givenHour * feeAmount
        : zoneObj.zone.price * feeAmount;
      const calcEst = (calcRate + calcFee).toFixed(2);

      setEstimatedPrice(calcEst);

      // if (!isNaN(calcEst) && calcEst > 0) {
      //   const eFixed = calcEst.toFixed(2);
      //   setEstimatedPrice(eFixed);
      // }
    } else {
      const calcDailyRate = Number(f);
      setEstimatedPrice(zoneObj.zone.price + calcDailyRate);
    }
  };

  useEffect(() => {
    setUpCosts(hours);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = async () => {
    let licensePlateData;

    setIsLoading(true);
    const newFees = roundUp(
      isHourly ? rate * hours * feeAmount : rate * feeAmount
    );
    if (isEmpty(vehiclePlate)) {
      setErrorMessage("Please provide a valid license plate");
      setIsLoading(false);
      return;
    }
    if (isEmpty(vehicleState)) {
      setErrorMessage("Please provide a valid state for license plate");
      setIsLoading(false);
      return;
    }

    try {
      const vehicleInfo = { vehiclePlate, vehicleState };
      const vehicleService = new VehicleService();
      const {
        data: { data },
      } = await vehicleService.get(vehicleInfo);

      if (data.status === "ok") {
        licensePlateData = data.content;

        setSession({
          ...sessionData,
          fees: newFees,
          vehicleMake: licensePlateData.make,
          vehicleModel: licensePlateData.model,
          vehicleYear: licensePlateData.year,
          vehicleDescription: licensePlateData.vin_data.NCSABodyType,
          startTime: new Date(),
        });
        setIsLoading(false);
        handleNext();
      }
    } catch (err) {
      setErrorMessage(
        "Please provide a valid license plate number and try again"
      );
      setIsLoading(false);
    }
  };

  // const handleHourChange = (hour) => {
  //   setColors((prev) => ({ [hour]: !prev[hour] }));
  //   setUpCosts(hour);

  //   setSession({
  //     ...sessionData,
  //     hours: hour,
  //   });
  // };

  const handleChange = (e) => {
    if (errorMessage) {
      setErrorMessage("");
    }

    setSession({ ...sessionData, [e.target.name]: e.target.value });
  };

  return (
    <Container>
      <ZoneSection>
        <ZoneLabel>ZONE</ZoneLabel>
        <ZoneNumber>{zoneObj?.zone.zoneNumber}</ZoneNumber>

        <LocationTitle>{zoneObj?.location.name}</LocationTitle>

        <InfoRow>
          <IconWrapper>
            <LocationPin size={18} />
          </IconWrapper>
          <InfoText>{zoneObj?.location.address}</InfoText>
        </InfoRow>
      </ZoneSection>

      <FormSection>
        <div className={classes.licenseInputDiv}>
          <TextField
            label={"License Plate Number"}
            name="vehiclePlate"
            classes={{ root: classes.plateNumberInput }}
            InputLabelProps={{
              style: { fontSize: "16px" },
            }}
            inputProps={{
              maxLength: 8,
              style: { textTransform: "uppercase" },
            }}
            variant="outlined"
            onChange={handleChange}
            value={vehiclePlate}
            disabled={isLoading}
            style={{ width: "60%" }}
          />{" "}
          <TextField
            label={"License State"}
            name="vehicleState"
            classes={{ root: classes.plateStateInput }}
            InputLabelProps={{
              style: { fontSize: "16px" },
            }}
            inputProps={{
              maxLength: 2,
              style: { textTransform: "uppercase" },
            }}
            variant="outlined"
            onChange={handleChange}
            value={vehicleState}
            disabled={isLoading}
            style={{ width: "35%" }}
          />{" "}
        </div>

        <PriceRow>
          <PriceLabel>Daily Rate:</PriceLabel>
          <PriceValue>{formatPrice(zoneObj.zone.price)}</PriceValue>
        </PriceRow>

        <PriceRow>
          <PriceLabel>Taxes & Fees:</PriceLabel>
          <PriceValue>{formatPrice(feeState)}</PriceValue>
        </PriceRow>

        <Divider />

        <TotalRow>
          <TotalLabel>Estimated Price:</TotalLabel>
          <TotalValue>{formatPrice(estimatedPrice)}</TotalValue>
        </TotalRow>

        {isLoading ? (
          <CircularProgress />
        ) : (
          <ContinueButton onClick={handleContinue}>
            <ButtonText>CONTINUE</ButtonText>
            &gt;
          </ContinueButton>
        )}

        {errorMessage && (
          <div
            style={{
              color: "red",
              fontSize: "14px",
              marginTop: "10px",
              textAlign: "center",
            }}
          >
            {errorMessage}
          </div>
        )}
      </FormSection>
    </Container>
  );
};

// const HoursRow = styled.div`
//   display: flex;
// `;

// const HoursCell = styled.div`
//   cursor: pointer;
//   display: flex;
//   align-items: center;
//   padding: 15px 40px;
//   border: 1px solid #000;
//   border-radius: 5px;
//   margin: 5px;
//   height: 60px;
//   &:hover {
//     background-color: #12b981;
//     color: #fff;
//     border: 1px solid #12b981;
//   }
//   background-color: ${(props) => (props.selected ? "#12b981" : "none")};
// `;

export const ContinueButton = styled.button`
  height: 50px;
  width: 100%;
  margin-top: 20px;
  cursor: pointer;
  border: none;
  border-radius: 5px;
  background-color: #12b981;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  transition: all 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94) 0s;
  &:hover {
    background-color: #0db17c;
  }
`;

// Styled Components
const Container = styled.div`
  width: 100%;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  overflow: hidden;
`;

const ZoneSection = styled.div`
  padding: 24px;
`;

const ZoneLabel = styled.h2`
  font-size: 14px;
  text-transform: uppercase;
  color: #6b7280;
  font-weight: 500;
  margin: 0 0 8px 0;
  letter-spacing: 0.05em;
`;

const ZoneNumber = styled.div`
  font-size: 96px;
  font-weight: bold;
  color: #12b981;
  line-height: 1;
  margin-bottom: 24px;
`;

const LocationTitle = styled.h3`
  font-size: 24px;
  text-transform: capitalize;
  font-weight: bold;
  color: gray;
  margin: 0 0 16px 0;
  padding-left: 12px;
  border-left: 4px solid #12b981;
  line-height: 1.2;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  width: 70%;
`;

const IconWrapper = styled.div`
  color: #12b981;
  margin-right: 12px;
  display: flex;
  align-items: center;
`;

const InfoText = styled.p`
  margin: 0;
  color: #4b5563;
  font-size: 12px;
`;

const FormSection = styled.div`
  padding: 24px;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const PriceLabel = styled.span`
  color: #4b5563;
  font-size: 16px;
`;

const PriceValue = styled.span`
  font-weight: 500;
  color: #1f2937;
  font-size: 16px;
`;

const Divider = styled.hr`
  border: 0;
  height: 1px;
  background-color: #e5e7eb;
  margin: 16px 0;
`;

const TotalRow = styled(PriceRow)`
  margin-top: 16px;
`;

const TotalLabel = styled.span`
  font-weight: bold;
  color: #1f2937;
  font-size: 18px;
`;

const TotalValue = styled.span`
  font-weight: bold;
  color: #12b981;
  font-size: 18px;
`;

// const ContinueButtonNew = styled.button`
//   width: 100%;
//   background-color: #12b981;
//   color: white;
//   border: none;
//   border-radius: 8px;
//   padding: 16px;
//   font-size: 16px;
//   font-weight: 600;
//   cursor: pointer;
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   margin-top: 24px;
//   transition: background-color 0.2s;

//   &:hover {
//     background-color: #12b981;
//   }
// `;

const ButtonText = styled.span`
  margin-right: 8px;
`;

// Location Pin Icon Component
const LocationPin = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default SessionInfoPage;
